/**
 * State 7 of 9 — agent offline (spec 01 §18, `PRODUCT.md` §4).
 *
 * **This is the one to remember.** `PRODUCT.md` §4's seventh row: *an employee who
 * did not work and an employee whose agent died are different people*, and a UI that
 * renders the second as a zero throws away — at the last step, in front of the
 * person who has to explain the difference — the distinction the whole backend is
 * built to preserve. `shiftFor` keeps `off_duty` apart from `unconfigured`, the
 * processor keeps `skipped` apart from `failed`, sessions keep `partial` apart from
 * `missing`; this component is where that discipline either survives or does not.
 *
 * It is enforced structurally: **there is no numeric prop.** A screen cannot pass
 * this component a `0` and have it render as "did no work", because it has nothing
 * to render a number into. The screen's own figure must be replaced by this state,
 * not annotated with it.
 *
 * **Driven by:** `Device.liveness` / `DeviceHealth.liveness` in `openapi.yaml` —
 * Task 19's computed liveness. Three properties of that value matter here:
 *
 *   - It is **computed on every read** from `last_heartbeat_at` and the clock, and
 *     never stored: "so that a scheduled sweep that has stopped running degrades the
 *     answer instead of freezing it". The verdict therefore lags pessimistically —
 *     a machine that is fine may read `stale` — which is the safe direction.
 *   - `stale` and `offline` are kept apart. `stale` is "silent longer than three
 *     heartbeat intervals but inside `WP_OFFLINE_AFTER`" — a dashboard shade and a
 *     leading indicator. `offline` is the one a human acts on and the one
 *     `wp_agent_offline_count` counts. Two words, two severities.
 *   - `online` is not a prop value. There is no state to render for a device that
 *     is fine.
 *
 * `last_heartbeat_at: null` means never heard from at all, which is a different
 * sentence again — and rendering it as a blank timestamp would be the same class of
 * mistake as rendering the outage as a zero.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../i18n';
import { formatInstantWithAge } from '../i18n/datetime';
import { StatusNote } from './StatusNote';

/** `DeviceLiveness` minus `online`: there is no state to render for a healthy device. */
export type OfflineLiveness = 'stale' | 'offline';

export interface AgentOfflineProps {
  readonly liveness: OfflineLiveness;
  /** `Device.last_heartbeat_at`. `null` — never heard from — is a value, not a blank. */
  readonly lastHeartbeatAt: string | null;
  /**
   * **This screen cannot read heartbeats at all** — a third sentence, added by Task
   * 42 and not a synonym for either of the first two.
   *
   * `GET /devices` and `GET /devices/{id}/health` require `workforce.manage_devices`,
   * which an employee does not hold, so `/my-activity` can observe that the agent
   * produced nothing for a day and cannot observe when the machine last spoke. The
   * three states are: a timestamp (we heard from it then), `null` (we have never
   * heard from it), and this (we are not permitted to know). Rendering the third as
   * the second would be the same class of mistake as rendering an outage as a zero —
   * an absence reported as a fact — one level down, so it gets its own line naming
   * the screen that does hold the answer.
   *
   * There is still **no numeric prop**. That guarantee is what this component is for.
   */
  readonly heartbeatUnavailable?: boolean;
  /** The company time zone from `SessionUser.timezone`. */
  readonly timezone: string;
  /** Hostname or device id. Falls back to "thiết bị này" so the sentence still reads. */
  readonly deviceLabel?: string;
  readonly actions?: ReactNode;
  readonly testId?: string;
}

export function AgentOffline({
  liveness,
  lastHeartbeatAt,
  timezone,
  deviceLabel,
  heartbeatUnavailable = false,
  actions,
  testId = 'state-agent-offline',
}: AgentOfflineProps): ReactNode {
  const { t, locale } = useI18n();
  const device = deviceLabel ?? t('state.agentOffline.device');
  // `offline` is acted on, `stale` is watched. Two severities, matching the two
  // sentences — never one shade of the same colour.
  const severity = liveness === 'offline' ? 'high' : 'medium';

  return (
    <StatusNote
      severity={severity}
      title={
        liveness === 'offline'
          ? t('state.agentOffline.offline.title')
          : t('state.agentOffline.stale.title')
      }
      testId={testId}
      actions={actions}
    >
      <p>
        {liveness === 'offline'
          ? t('state.agentOffline.offline.body', { device })
          : t('state.agentOffline.stale.body', { device })}
      </p>
      <p className="wp-small wp-muted">
        {heartbeatUnavailable
          ? t('state.agentOffline.heartbeatUnavailable')
          : lastHeartbeatAt === null
            ? t('state.agentOffline.neverHeard')
            : t('state.agentOffline.lastHeartbeat', {
                when: formatInstantWithAge(lastHeartbeatAt, locale, timezone, t('common.never')),
              })}
      </p>
    </StatusNote>
  );
}
